'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { BackendAuth } from '@/lib/auth-backend';
import { getMenuIcon } from '@/lib/menu-icons';

// Entity Context for KAIE context-sensitive menus
const EntityContext = createContext();

/**
 * Replace placeholders in menu link with actual values.
 * Supports: {id} = Entity ID (the only placeholder we still substitute).
 *
 * NOTE: We DELIBERATELY no longer substitute `{org}` — organisation must come
 * from the authenticated user (JWT), never from URL input. Menu links that
 * still contain `{org}/...` are stripped of that segment so the URL stays clean.
 */
function replaceLinkPlaceholders(link, organisation, entityId) {
  if (!link || typeof link !== 'string') return link;

  // Drop any legacy `{org}/` prefix from manifest-defined links — backend
  // derives org from JWT, no need to encode it in the URL.
  link = link.replace(/\{org\}\//g, '').replace(/\{org\}/g, '');

  if (entityId) {
    link = link.replace(/\{id\}/g, String(entityId));
  }

  return link;
}

/**
 * Convert context menu items from backend to frontend format
 */
function convertContextMenuToFrontend(menuItems, userLang = 'de', organisation = null, entityId = null) {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  return menuItems.map((item) => {
    const localizedTitle = item.name_de || item.name || '';

    const menuItem = {
      title: localizedTitle,
      target: item.window === '_blank' ? '_blank' : undefined,
      module: item.module,
      id: item.id,
      sequence: item.sequence,
      link_type: item.link_type || '0',
      original_link: item.link,
      badge_text: item.badge_text ?? null,
      divider_before: item.divider_before === 1 || item.divider_before === true,
      depth: item.depth || 0
    };

    // Handle disabled items
    if (item.is_disabled === 1 || item.is_disabled === true) {
      menuItem.disabled = true;
    }

    // Handle headings
    if (item.is_heading === 1 || item.is_heading === true) {
      return {
        heading: localizedTitle,
        type: 'header',
        id: item.id
      };
    }

    // Resolve icon
    const iconKey = (typeof item.icon === 'string' && item.icon.trim() !== '')
      ? item.icon
      : (typeof item.name === 'string' ? item.name : '');
    if (iconKey) {
      menuItem.icon = getMenuIcon(iconKey);
    }

    // Handle path - convert legacy paths to React routes with placeholder replacement
    if (item.link && item.link !== '#' && item.link !== '') {
      // Replace {org} and {id} placeholders in KAIE context menu links
      const processedLink = replaceLinkPlaceholders(item.link, organisation, entityId);
      menuItem.path = processedLink;
    }

    // Recursively convert children
    if (item.children && Array.isArray(item.children) && item.children.length > 0) {
      menuItem.children = convertContextMenuToFrontend(item.children, userLang, organisation, entityId);
    }

    return menuItem;
  });
}

/**
 * EntityProvider - Manages KAIE entity context state
 *
 * Provides:
 * - selectedEntity: { type: 'ie'|'hie', id: number, name: string, organisation: number }
 * - contextMenu: Converted menu items for sidebar display
 * - setSelectedEntity: Function to select an entity
 * - clearEntity: Function to clear the selection
 * - isLoading: Loading state
 */
// Read entity ID ONLY from URL pathname — never from query params.
// Pattern matches /kaie/<anyOrg>/(ie|hie|gie)/<id>(/...optional sub-area).
// The org in the path is just a routing convenience; the AUTHORITATIVE org
// comes from the backend lookup (which is JWT-protected and validates user
// has access to that IE). User-controlled URL params are intentionally NOT
// accepted as a source of truth for org/type/name — those are derived from
// the backend, so the URL cannot be tampered with to leak other-tenant data.
// Map URL slug → entity-type for sidebar show_when filter.
//   kaie-ie / ie  → 'ie'
//   kaie-hie / hie → 'hie'
//   kaie-gastro / gie → 'gie'
function _slugToEntityType(slug) {
  if (!slug) return 'ie';
  if (slug === 'kaie-hie' || slug === 'hie') return 'hie';
  if (slug === 'kaie-gastro' || slug === 'kaie-gie' || slug === 'gie') return 'gie';
  return 'ie';
}

// Read entity ID + type from URL pathname.
// Returns { id, type } or null. The slug-derived `type` is an initial hint so the
// sidebar's show_when filter can render the right sub-menu BEFORE the backend
// round-trip completes; _fetchEntityFull later confirms/overrides from the DB.
function _readEntityFromPath() {
  if (typeof window === 'undefined') return null;
  try {
    const path = window.location.pathname;
    // 1. Container-URLs: /app/kaie-(ie|hie|gastro)/<id>[/<sub-area>]  (Phase 5)
    let m = path.match(/^\/app\/(kaie-(?:ie|hie|gastro))\/(\d+)(?:\/|$)/);
    if (m) return { id: parseInt(m[2], 10), type: _slugToEntityType(m[1]) };
    // 2. Core clean URL: /kaie/(ie|hie|gie)/<id>[/<sub-area>]  (Legacy — Phase 6 entfernen)
    m = path.match(/^\/kaie\/(ie|hie|gie)\/(\d+)(?:\/|$)/);
    if (m) return { id: parseInt(m[2], 10), type: _slugToEntityType(m[1]) };
    // 3. Very-Legacy: /kaie/<org>/(ie|hie|gie)/<id>
    m = path.match(/^\/kaie\/\d+\/(ie|hie|gie)\/(\d+)(?:\/|$)/);
    if (m) return { id: parseInt(m[2], 10), type: _slugToEntityType(m[1]) };
    return null;
  } catch { return null; }
}

// Backwards-compat wrapper for places that still want just the id.
function _readEntityIdFromPath() {
  const e = _readEntityFromPath();
  if (!e || !Number.isFinite(e.id) || e.id <= 0) return null;
  return e.id;
}

// Fetch full entity (type + name + organisation) from KAIE backend.
// This is the ONLY source of truth for type/name/org — never trust URL params.
async function _fetchEntityFull(id) {
  if (!id) return null;
  try {
    const tok = (typeof window !== 'undefined')
      ? (localStorage.getItem('access_token') || localStorage.getItem('auth_token'))
      : null;
    const headers = { Accept: 'application/json' };
    if (tok) headers.Authorization = `Bearer ${tok}`;
    const r = await fetch(`/api/kaie/iev/indirekteinleiter/${id}`, {
      credentials: 'include',
      headers,
    });
    if (!r.ok) return null;
    const j = await r.json();
    const data = j?.data ?? j;
    const t = data?.type;
    // Map IE-type column (0/1 = betrieblich, 5 = häuslich, 10 = Gastro)
    // to the entity-type the sidebar `show_when` filter expects.
    const entityType = t === 5 ? 'hie' : t === 10 ? 'gie' : 'ie';
    return {
      id,
      type: entityType,
      name: data?.name || data?.stammdaten_name || '',
      organisation: data?.organisation
        ?? data?.organisation_id
        ?? data?.kontakt_organisation
        ?? null,
    };
  } catch { return null; }
}

export function EntityProvider({ children }) {
  // URL pathname is the ONLY source of truth for the entity ID.
  // type / name / organisation come from the backend lookup (auth-protected) —
  // never from URL query params (would be tamperable).
  const [selectedEntity, setSelectedEntityState] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // One-shot legacy cleanup: prior versions stored entity state in localStorage
  // under `kaie_current_*`. Wipe leftovers so they can never re-hydrate.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      ['kaie_current_ie_id', 'kaie_current_ie_type', 'kaie_current_org_id', 'kaie_current_ie_name']
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  }, []);

  // React to client-side navigation (next/navigation tracks pathname changes from
  // <Link>, router.push, router.replace; popstate alone doesn't cover those).
  // We intentionally do NOT use searchParams — query params are ignored.
  const pathname = usePathname();

  // Re-evaluate entity from path whenever pathname changes.
  // Navigating away from /app/kaie-*/<id> or /kaie/<org>/(ie|hie|gie)/<id>
  // → entity becomes null automatically.
  useEffect(() => {
    const next = _readEntityFromPath();
    setSelectedEntityState(prev => {
      // Drop entity when leaving the entity-detail route entirely.
      if (!next) return prev ? null : prev;
      // Same id → keep current (already-resolved type/name/org), but if the URL
      // slug now reports a different type than what we have, trust the URL hint
      // for the sidebar filter until the backend lookup confirms.
      if (prev && prev.id === next.id) {
        if (prev.type === next.type) return prev;
        return { ...prev, type: next.type };
      }
      // New id → set placeholder with URL-slug-derived type so the sidebar
      // show_when filter renders the right sub-menu IMMEDIATELY; the hydration
      // effect later confirms/overrides from the auth-protected backend.
      return { id: next.id, type: next.type, name: '', organisation: null };
    });
  }, [pathname]);

  // Whenever the ID changes, fetch full entity (type/name/organisation) from
  // the auth-protected backend endpoint. This is the ONLY way these fields get set.
  useEffect(() => {
    const id = selectedEntity?.id;
    if (!id) return;
    // Skip if we already have everything resolved for this id.
    if (selectedEntity.name && selectedEntity.organisation && selectedEntity.type) {
      // Validate name isn't a fallback like "Indirekteinleiter #12345"
      const n = selectedEntity.name;
      const looksFallback = /^ie\s*#?\d+$/i.test(n)
        || /^hie\s*#?\d+$/i.test(n)
        || /^indirekteinleiter\s*#?\d+$/i.test(n)
        || /^h(ae|ä)usliche?s?\s*abwasser\s*#?\d+$/i.test(n);
      if (!looksFallback) return;
    }
    let cancelled = false;
    _fetchEntityFull(id).then(full => {
      if (cancelled || !full) return;
      setSelectedEntityState(prev => prev && prev.id === full.id ? { ...prev, ...full } : prev);

      // If the URL slug doesn't match the entity's real type (backend is authoritative),
      // redirect to the correct container so the iframe loads the right detail-pages
      // AND the sidebar's show_when filter matches.
      // e.g. user opens /app/kaie-ie/8670 but the entity is type='hie' → redirect
      // to /app/kaie-hie/8670.
      if (typeof window !== 'undefined') {
        try {
          const path = window.location.pathname;
          const m = path.match(/^\/app\/kaie-(ie|hie|gastro)\/(\d+)(\/.*)?$/);
          if (m) {
            const urlSlugType = m[1] === 'gastro' ? 'gie' : m[1];
            const realType = full.type;
            if (realType && realType !== urlSlugType) {
              const realSlug = realType === 'hie' ? 'kaie-hie'
                : realType === 'gie' ? 'kaie-gastro'
                : 'kaie-ie';
              const newPath = `/app/${realSlug}/${full.id}${m[3] || ''}` + window.location.search;
              window.history.replaceState({}, '', newPath);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          }
        } catch {}
      }
    });
    return () => { cancelled = true; };
  }, [selectedEntity?.id]);

  // Listen for in-page entity changes (CustomEvent dispatched by sub-components
  // that need to programmatically set the entity, e.g. iframe→parent comms) +
  // browser back/forward.
  // CustomEvent.detail MUST contain only `id` — the provider will hydrate the rest
  // from the backend; we intentionally ignore caller-supplied type/name/org.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleEntityChanged = (e) => {
      const detail = e.detail;
      if (detail && detail.id) {
        const idNum = parseInt(detail.id, 10);
        if (!Number.isFinite(idNum) || idNum <= 0) return;
        setSelectedEntityState({ id: idNum, type: 'ie', name: '', organisation: null });
      } else if (detail === null) {
        setSelectedEntityState(null);
      }
    };
    const handlePopState = () => {
      const next = _readEntityFromPath();
      setSelectedEntityState(prev => {
        if (!next) return null;
        if (prev && prev.id === next.id) {
          if (prev.type === next.type) return prev;
          return { ...prev, type: next.type };
        }
        return { id: next.id, type: next.type, name: '', organisation: null };
      });
    };
    window.addEventListener('xoffi:entity-changed', handleEntityChanged);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('xoffi:entity-changed', handleEntityChanged);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Load context menu when entity changes.
  // The backend endpoint requires `organisation` as a non-empty integer query
  // param — it 422s on empty. We therefore wait until `_fetchEntityFull` has
  // populated organisation from the auth-protected backend before requesting
  // the context menu. The MAIN sidebar (which renders the kaie-ie/hie/gastro
  // sub-menu) does NOT depend on this contextMenu state — it filters via
  // show_when on the regular menu structure — so skipping early is safe.
  useEffect(() => {
    if (!selectedEntity) {
      setContextMenu(null);
      setError(null);
      return;
    }
    if (!selectedEntity.organisation) {
      // Not yet hydrated — _fetchEntityFull will fire and trigger another
      // pass of this effect once organisation is set.
      return;
    }

    const loadContextMenu = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await BackendAuth.apiCallJson(
          `/api/menu/context/${selectedEntity.type}/${selectedEntity.id}?organisation=${selectedEntity.organisation}`
        );

        if (response.success && response.menu) {
          // Pass organisation and entity ID for placeholder replacement
          const convertedMenu = convertContextMenuToFrontend(
            response.menu,
            'de',
            selectedEntity.organisation,
            selectedEntity.id
          );
          setContextMenu(convertedMenu);

          // Update entity info from response, but NEVER overwrite a real name
          // with the backend's fallback "Indirekteinleiter #<id>" / "Häusliches Abwasser #<id>".
          // The backend currently always returns the fallback regardless of real DB name;
          // _fetchEntityName(...) handles the real lookup separately.
          if (response.entity?.name) {
            const incoming = response.entity.name;
            const isFallback = /^ie\s*#?\d+$/i.test(incoming)
              || /^hie\s*#?\d+$/i.test(incoming)
              || /^indirekteinleiter\s*#?\d+$/i.test(incoming)
              || /^h(ae|ä)usliche?s?\s*abwasser\s*#?\d+$/i.test(incoming);
            setSelectedEntityState(prev => {
              if (!prev) return prev;
              const prevLooksFallback = !prev.name
                || /^ie\s*#?\d+$/i.test(prev.name)
                || /^hie\s*#?\d+$/i.test(prev.name)
                || /^indirekteinleiter\s*#?\d+$/i.test(prev.name)
                || /^h(ae|ä)usliche?s?\s*abwasser\s*#?\d+$/i.test(prev.name);
              // Only overwrite if incoming is BETTER (real name) than prev.
              if (!isFallback) return { ...prev, name: incoming };
              if (prevLooksFallback) return { ...prev, name: incoming };
              return prev;
            });
          }
        } else {
          setError(response.error || 'Failed to load context menu');
          setContextMenu(null);
        }
      } catch (err) {
        console.error('[EntityProvider] Failed to load context menu:', err);
        setError(err.message || 'Network error');
        setContextMenu(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadContextMenu();
  }, [selectedEntity?.type, selectedEntity?.id, selectedEntity?.organisation]);

  // Set selected entity.
  // Callers should pass ONLY `{ id }` — type, name and organisation are loaded from
  // the auth-protected backend. Passing them is allowed for transient display while
  // the backend lookup is in flight, but the hydration effect will overwrite them.
  const setSelectedEntity = useCallback((entity) => {
    if (!entity) {
      setSelectedEntityState(null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('xoffi:entity-changed', { detail: null }));
      }
      return;
    }
    if (!entity.id) {
      console.error('[EntityProvider] setSelectedEntity called without id:', entity);
      return;
    }
    const idNum = parseInt(entity.id, 10);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    const next = {
      id: idNum,
      type: entity.type || 'ie',
      name: entity.name || '',
      organisation: entity.organisation || null,
    };
    setSelectedEntityState(next);
    if (typeof window !== 'undefined') {
      // Only broadcast the id — see handleEntityChanged in this same provider.
      window.dispatchEvent(new CustomEvent('xoffi:entity-changed', { detail: { id: idNum } }));
    }
  }, []);

  // Clear entity selection
  const clearEntity = useCallback(() => {
    setSelectedEntityState(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('xoffi:entity-changed', { detail: null }));
    }
  }, []);

  const value = {
    selectedEntity,
    setSelectedEntity,
    clearEntity,
    contextMenu,
    isLoading,
    error
  };

  return (
    <EntityContext.Provider value={value}>
      {children}
    </EntityContext.Provider>
  );
}

/**
 * Hook to access entity context
 */
export function useEntityContext() {
  const context = useContext(EntityContext);
  if (!context) {
    // Return safe defaults when used outside provider
    return {
      selectedEntity: null,
      setSelectedEntity: () => {},
      clearEntity: () => {},
      contextMenu: null,
      isLoading: false,
      error: null
    };
  }
  return context;
}

export default EntityContext;
